import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Linkedin, Github } from 'lucide-react';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';
import { EMAILJS_CONFIG } from '../config/emailjs';

const Contact = () => {
    const formRef = useRef<HTMLFormElement>(null);
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Inicializar EmailJS
    useEffect(() => {
        emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!formRef.current) return;

        try {
            const formData = new FormData(formRef.current);
            const templateParams = {
                from_name: formData.get('name'),
                from_email: formData.get('email'),
                subject: formData.get('subject'),
                message: formData.get('message')
            };

            console.log('🔧 Configuração usada:', EMAILJS_CONFIG);
            console.log('📧 Dados do formulário:', templateParams);

            // Enviar e-mail usando o mesmo método que funcionou no teste
            const response = await emailjs.send(
                EMAILJS_CONFIG.SERVICE_ID,
                EMAILJS_CONFIG.TEMPLATE_ID,
                templateParams,
                EMAILJS_CONFIG.PUBLIC_KEY
            );

            console.log('✅ Email enviado com sucesso:', response);
            
            // Toast de sucesso
            toast.success('🎉 Mensagem enviada com sucesso! Obrigado pelo contato.', {
                duration: 4000,
                position: 'top-right',
                style: {
                    background: '#10B981',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '500'
                },
                iconTheme: {
                    primary: '#fff',
                    secondary: '#10B981'
                }
            });
            
            formRef.current.reset();
        } catch (error) {
            console.error('❌ Erro ao enviar email:', error);
            
            // Toast de erro
            toast.error('❌ Erro ao enviar mensagem. Tente novamente ou entre em contato diretamente.', {
                duration: 5000,
                position: 'top-right',
                style: {
                    background: '#EF4444',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '500'
                },
                iconTheme: {
                    primary: '#fff',
                    secondary: '#EF4444'
                }
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: Mail,
            label: t('contact.email'),
            value: "contato.gustavo.naka@gmail.com",
            href: "mailto:contato.gustavo.naka@gmail.com",
            color: "from-blue-400 to-blue-600"
        },
        {
            icon: MapPin,
            label: t('contact.location'),
            value: t('contact.locationValue'),
            href: "#",
            color: "from-purple-400 to-purple-600"
        }
    ];

    const socialLinks = [
        {
            icon: Linkedin,
            href: "https://www.linkedin.com/in/gustavo-nakamura-339628246/",
            label: "LinkedIn",
            color: "hover:text-blue-400"
        },
        {
            icon: Github,
            href: "https://github.com/gustavonakamura",
            label: "GitHub",
            color: "hover:text-purple-500" // Alterado de hover:text-gray-400 para hover:text-purple-500
        }
    ];

    return (
        <section id="contact" className="py-20 relative overflow-hidden">
            {/* Background effects - estático, sem transform vinculado ao scroll */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <motion.h2
                    className="text-4xl md:text-6xl font-bold text-center text-white mb-8"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    {t('contact.title')}
                </motion.h2>
                
                <motion.p 
                    className="text-xl text-gray-300 text-center max-w-3xl mx-auto mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                   {t('contact.description')}
                </motion.p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                    {/* Contact Info */}
                    <motion.div 
                        className="space-y-6"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="text-2xl font-bold text-white mb-6">{t('contact.info')}</h3>
                        {contactInfo.map((info, index) => (
                            <motion.a
                                key={info.label}
                                href={info.href}
                                className="flex items-center space-x-4 p-4 glass rounded-xl hover:bg-white/10 transition-all duration-300 group"
                                whileHover={{ scale: 1.02, x: 10 }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className={`p-3 rounded-lg bg-gradient-to-r ${info.color} group-hover:scale-110 transition-transform duration-300`}>
                                    <info.icon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-gray-400 text-sm">{info.label}</p>
                                    <p className="text-white font-medium">{info.value}</p>
                                </div>
                            </motion.a>
                        ))}
                    </motion.div>

                    {/* Contact Form */}
                    <motion.div 
                        className="glass p-8 rounded-2xl"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="text-2xl font-bold text-white mb-6">{t('contact.form.title')}</h3>
                        <form ref={formRef} className="space-y-6" onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <label className="block text-white text-sm font-medium mb-2">{t('contact.form.name')}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                        placeholder={t('contact.form.namePlaceholder')}
                                    />
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                    viewport={{ once: true }}
                                >
                                    <label className="block text-white text-sm font-medium mb-2">{t('contact.form.email')}</label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                        placeholder={t('contact.form.emailPlaceholder')}
                                    />
                                </motion.div>
                            </div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.7 }}
                                viewport={{ once: true }}
                            >
                                <label className="block text-white text-sm font-medium mb-2">{t('contact.form.subject')}</label>
                                <input
                                    type="text"
                                    name="subject"
                                    required
                                    className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300"
                                    placeholder={t('contact.form.subjectPlaceholder')}
                                />
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.8 }}
                                viewport={{ once: true }}
                            >
                                <label className="block text-white text-sm font-medium mb-2">{t('contact.form.message')}</label>
                                <textarea
                                    rows={4}
                                    name="message"
                                    required
                                    className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all duration-300 resize-none"
                                    placeholder={t('contact.form.messagePlaceholder')}
                                ></textarea>
                            </motion.div>
                            <motion.button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full px-8 py-4 border-2 border-white/30 text-white rounded-lg font-semibold glass hover:bg-white/10 transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.9 }}
                                viewport={{ once: true }}
                            >
                                {isSubmitting ? 'Enviando...' : t('contact.form.send')}
                            </motion.button>
                        </form>
                    </motion.div>
                </div>

                {/* Social Links */}
                <motion.div 
                    className="text-center"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    viewport={{ once: true }}
                >
                    <h3 className="text-xl font-bold text-white mb-6">{t('contact.social')}</h3>
                    <div className="flex justify-center space-x-6">
                        {socialLinks.map((social, index) => (
                            <motion.a
                                key={social.label}
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`p-4 glass rounded-full text-gray-400 ${social.color} transition-all duration-300 group`}
                                whileHover={{ scale: 1.1, y: -5 }}
                                whileTap={{ scale: 0.9 }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <social.icon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                            </motion.a>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Contact;
